/**
 * Prestations : les pages qui portent le référencement de l'activité.
 *
 * C'est l'écran où le garde-fou éditorial compte le plus. Une page de
 * prestation publiée sous 300 mots, sans réponse directe ou avec moins de trois
 * questions fréquentes fait **échouer la publication du site entier**. Les
 * alertes sont donc affichées à côté du texte pendant qu'on l'écrit, jamais
 * gardées pour le moment de publier.
 */
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ContenuAtelier,
  PiedFormulaire,
  Section,
  type Selection,
  champsModifies,
  erreurChamp,
  slugifier,
  useFiche,
} from "../composants/ContenuAtelier";
import {
  type CategoriePrestation,
  type Prestation,
  useEnregistrer,
  usePrestations,
  useQualite,
  useSupprimer,
} from "../composants/ContenuDonnees";
import { AlertesQualite, ResumeQualite, compterMotsHtml } from "../composants/ContenuQualite";
import { ChampsSeo } from "../composants/ContenuSeo";
import { EditeurRiche } from "../composants/EditeurRiche";

type Brouillon = {
  slug: string;
  nom: string;
  categorie: CategoriePrestation;
  resume: string;
  reponseDirecte: string;
  contenu: string;
  icone: string;
  ordre: number;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
};

/** Fiche vierge : aucun texte d'exemple, rien qui ressemble à une donnée réelle. */
const VIDE: Brouillon = {
  slug: "",
  nom: "",
  categorie: "interieur",
  resume: "",
  reponseDirecte: "",
  contenu: "",
  icone: "",
  ordre: 0,
  metaTitle: "",
  metaDescription: "",
  isPublished: false,
};

const CATEGORIES: Array<{ valeur: CategoriePrestation; libelle: string }> = [
  { valeur: "interieur", libelle: "Rénovation intérieure" },
  { valeur: "metallerie", libelle: "Métallerie" },
];

function depuis(prestation: Prestation): Brouillon {
  return {
    slug: prestation.slug,
    nom: prestation.nom,
    categorie: prestation.categorie,
    resume: prestation.resume,
    reponseDirecte: prestation.reponseDirecte,
    contenu: prestation.contenu,
    icone: prestation.icone,
    ordre: prestation.ordre,
    metaTitle: prestation.metaTitle,
    metaDescription: prestation.metaDescription,
    isPublished: prestation.isPublished,
  };
}

/** Chemin public de la page, tel que le rapport de qualité le nomme. */
const cheminPublic = (slug: string) => `/${slug}/`;

export function ContenuPrestations() {
  const prestations = usePrestations();
  const qualite = useQualite();
  const enregistrement = useEnregistrer<Prestation>("prestations");
  const suppression = useSupprimer("prestations");
  const fiche = useFiche<Brouillon>(VIDE);

  const liste = prestations.data ?? [];
  const courante =
    typeof fiche.selection === "number"
      ? liste.find((prestation) => prestation.id === fiche.selection)
      : undefined;

  const enDefaut = new Set(
    (qualite.data?.pages ?? [])
      .filter((page) => page.type === "prestation" && page.manquements.length > 0)
      .map((page) => page.chemin),
  );

  function selectionner(cible: Selection) {
    enregistrement.reset();
    suppression.reset();

    if (cible === null) return fiche.fermer();
    if (cible === "nouveau") return fiche.ouvrir("nouveau", VIDE);

    const prestation = liste.find((element) => element.id === cible);
    if (prestation) fiche.ouvrir(cible, depuis(prestation));
  }

  function enregistrer() {
    const id = typeof fiche.selection === "number" ? fiche.selection : null;
    const champs = id === null ? fiche.brouillon : champsModifies(fiche.brouillon, fiche.reference);
    if (Object.keys(champs).length === 0) return;

    enregistrement.mutate(
      { id, champs },
      {
        onSuccess: (prestation) => {
          if (prestation) fiche.confirmer(prestation.id, depuis(prestation));
        },
      },
    );
  }

  function supprimer() {
    if (typeof fiche.selection !== "number") return;
    suppression.mutate(fiche.selection, { onSuccess: () => fiche.fermer() });
  }

  const erreur = enregistrement.error ?? suppression.error ?? null;
  const brouillon = fiche.brouillon;

  return (
    <ContenuAtelier
      intro="Les pages de prestation portent l'essentiel du référencement. Chacune doit tenir ses trois cents mots, sa réponse directe et ses trois questions fréquentes, faute de quoi la publication du site échoue."
      entete={<ResumeQualite filtre="prestation" />}
      entrees={liste.map((prestation) => ({
        id: prestation.id,
        libelle: prestation.nom,
        secondaire: `/${prestation.slug}/`,
        publie: prestation.isPublished,
        enDefaut: enDefaut.has(cheminPublic(prestation.slug)),
      }))}
      selection={fiche.selection}
      surSelection={selectionner}
      libelleNouveau="Nouvelle prestation"
      modifie={fiche.modifie}
      chargement={prestations.isPending}
      erreur={prestations.error ?? null}
      invite="Choisissez une prestation à modifier, ou créez-en une."
    >
      <Stack spacing={3}>
        <AlertesQualite
          chemin={courante ? cheminPublic(courante.slug) : null}
          type="prestation"
          motsBrouillon={compterMotsHtml(brouillon.reponseDirecte, brouillon.contenu)}
          motsEnregistres={
            courante ? compterMotsHtml(courante.reponseDirecte, courante.contenu) : 0
          }
        />

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={4}>
            <Section titre="Identité">
              <TextField
                fullWidth
                label="Nom de la prestation"
                value={brouillon.nom}
                onChange={(evenement) => {
                  const nom = evenement.target.value;
                  // Le slug n'est proposé que sur une fiche neuve : le modifier
                  // ensuite changerait l'URL publique et casserait les liens
                  // déjà indexés.
                  fiche.modifier(
                    fiche.selection === "nouveau" ? { nom, slug: slugifier(nom) } : { nom },
                  );
                }}
                error={erreurChamp(erreur, "nom") !== undefined}
                helperText={erreurChamp(erreur, "nom")}
              />

              <TextField
                fullWidth
                label="Slug (URL)"
                value={brouillon.slug}
                onChange={(evenement) => fiche.modifier({ slug: evenement.target.value })}
                error={erreurChamp(erreur, "slug") !== undefined}
                helperText={
                  erreurChamp(erreur, "slug") ??
                  (fiche.selection === "nouveau"
                    ? "Minuscules, chiffres et tirets. Il fixe l'adresse publique de la page."
                    : "Modifier ce slug change l'adresse publique : les liens et les positions acquises pointeront dans le vide.")
                }
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">delsa.pro/</InputAdornment>,
                  },
                }}
              />

              <TextField
                select
                fullWidth
                label="Catégorie"
                value={brouillon.categorie}
                onChange={(evenement) =>
                  fiche.modifier({ categorie: evenement.target.value as CategoriePrestation })
                }
              >
                {CATEGORIES.map((categorie) => (
                  <MenuItem key={categorie.valeur} value={categorie.valeur}>
                    {categorie.libelle}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Icône"
                  value={brouillon.icone}
                  onChange={(evenement) => fiche.modifier({ icone: evenement.target.value })}
                  helperText="Identifiant du pictogramme utilisé par le site."
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Ordre d'affichage"
                  value={brouillon.ordre}
                  onChange={(evenement) =>
                    fiche.modifier({ ordre: Number(evenement.target.value) || 0 })
                  }
                />
              </Stack>

              <FormControlLabel
                control={
                  <Switch
                    checked={brouillon.isPublished}
                    onChange={(evenement) =>
                      fiche.modifier({ isPublished: evenement.target.checked })
                    }
                  />
                }
                label="Publier cette page"
              />
              <Typography variant="caption" color="text.secondary">
                Une page publiée en défaut de qualité éditoriale fait échouer la publication du
                site. Un brouillon fautif, lui, ne bloque rien.
              </Typography>
            </Section>

            <Section titre="Rédaction">
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Résumé"
                value={brouillon.resume}
                onChange={(evenement) => fiche.modifier({ resume: evenement.target.value })}
                error={erreurChamp(erreur, "resume") !== undefined}
                helperText={
                  erreurChamp(erreur, "resume") ??
                  "Phrase de présentation reprise dans les listes et dans llms.txt."
                }
              />

              <EditeurRiche
                libelle="Réponse directe"
                valeur={brouillon.reponseDirecte}
                surChangement={(html) => fiche.modifier({ reponseDirecte: html })}
                hauteurMin={140}
                aide="Le bloc que les moteurs génératifs citent tel quel. Obligatoire, et différent de celui des autres pages : deux réponses trop proches sont comptées comme du remplissage."
              />

              <EditeurRiche
                libelle="Contenu de la page"
                valeur={brouillon.contenu}
                surChangement={(html) => fiche.modifier({ contenu: html })}
                hauteurMin={320}
                aide="Titres, gras, italique, listes et liens. Le site n'affichera rien d'autre."
              />
            </Section>

            <Section titre="Référencement">
              <ChampsSeo
                metaTitle={brouillon.metaTitle}
                metaDescription={brouillon.metaDescription}
                surTitre={(metaTitle) => fiche.modifier({ metaTitle })}
                surDescription={(metaDescription) => fiche.modifier({ metaDescription })}
                chemin={cheminPublic(brouillon.slug || "…")}
                erreurTitre={erreurChamp(erreur, "metaTitle")}
                erreurDescription={erreurChamp(erreur, "metaDescription")}
              />
            </Section>
          </Stack>

          <PiedFormulaire
            modifie={fiche.modifie}
            enregistrement={enregistrement.isPending}
            surEnregistrer={enregistrer}
            surSupprimer={typeof fiche.selection === "number" ? supprimer : undefined}
            suppression={suppression.isPending}
            quoi="cette prestation"
            erreur={erreur}
            succes={fiche.succes}
          />
        </Paper>
      </Stack>
    </ContenuAtelier>
  );
}
